import { z } from 'zod/v4'

import { defineScreen, type ScreenData, type ScreenQuery } from '../define-screen.js'
import { freshnessSchema } from '../freshness.js'

import { auditCursorSchema, boundedCountSchema, cursorPageSchema } from './audit-common.js'

export const AUDIT_ACCESS_DEFAULT_LIMIT = 50
export const AUDIT_ACCESS_MAX_LIMIT = 100

/** The closed vocabularies of `bat.auth_audit_log`; a backend test compares each one with the constants of the schema. */
export const AUTH_AUDIT_CATEGORIES = [
  'authentication',
  'security',
  'mfa',
  'session',
  'recovery',
  'admin',
] as const
export const AUTH_AUDIT_OUTCOMES = ['success', 'failure', 'partial'] as const
export const AUTH_AUDIT_RISK_SCORES = ['low', 'medium', 'high', 'critical'] as const

/** The filters of the tab ARE its URL: what the URL cannot say the server refuses (strict), and the other way round. */
export const auditAccessQuerySchema = z.strictObject({
  /** Free text with a shape (39 types live only in the backend schema): an unknown type matches no row. */
  eventType: z
    .string()
    .regex(/^[a-z][a-z_]{0,49}$/)
    .optional(),
  category: z.enum(AUTH_AUDIT_CATEGORIES).optional(),
  outcome: z.enum(AUTH_AUDIT_OUTCOMES).optional(),
  /** The subject of the event (not the actor), matched by content without regard to case. */
  userEmail: z
    .string()
    .trim()
    .min(1)
    .max(254)
    // Postgres text cannot hold NUL: it would be a 500 instead of a 400.
    .refine((text) => !text.includes('\u0000'), 'userEmail cannot contain a NUL character')
    .optional(),
  /** Exact match. */
  ip: z.string().trim().min(1).max(45).optional(),
  riskScore: z.enum(AUTH_AUDIT_RISK_SCORES).optional(),
  range: z.enum(['24h']).optional(),
  cursor: auditCursorSchema.optional(),
  limit: z.coerce
    .number<number>()
    .int()
    .min(1)
    .max(AUDIT_ACCESS_MAX_LIMIT)
    .default(AUDIT_ACCESS_DEFAULT_LIMIT),
})

/**
 * One row of `bat.auth_audit_log`. `category` and `outcome` are free text in the RESPONSE (as `action` in «Activity»):
 * an old value outside the enum must not turn the whole screen into a 500. `riskScore` is free text too (the column is
 * a varchar(20)): the audit service writes the four values of `AUTH_AUDIT_RISK_SCORES` and the enum only filters the
 * QUERY; a value the screen does not know is shown as it is. No row carries the name of a person.
 */
export const accessEventRowSchema = z.strictObject({
  id: z.guid(),
  occurredAt: z.iso.datetime(),
  eventType: z.string().min(1).max(50),
  category: z.string().min(1).max(20),
  outcome: z.string().min(1).max(20),
  userId: z.guid().nullable(),
  userEmail: z.string().nullable(),
  actorEmail: z.string().nullable(),
  tenantId: z.guid().nullable(),
  ip: z.string().nullable(),
  /** Cut to 512 characters in the SELECT: a user agent is client-controlled. */
  userAgent: z.string().max(512).nullable(),
  country: z.string().nullable(),
  city: z.string().nullable(),
  riskScore: z.string().min(1).max(20).nullable(),
  failureReason: z.string().nullable(),
})
export type AccessEventRow = z.infer<typeof accessEventRowSchema>

export const adminAuditAccessScreen = defineScreen({
  id: 'admin.audit.access',
  path: '/api/screens/admin/audit/access',
  access: 'super_admin',
  query: auditAccessQuerySchema,
  response: z.strictObject({
    counts: z.strictObject({
      total: boundedCountSchema,
      /** Of the last 24 hours. */
      failedLogins24h: boundedCountSchema,
      /** Historical totals by level. */
      riskCritical: boundedCountSchema,
      riskHigh: boundedCountSchema,
    }),
    /** How many rows match the filters; null on every page but the first and when no filter is on. */
    matching: boundedCountSchema.nullable(),
    page: cursorPageSchema(accessEventRowSchema),
    freshness: freshnessSchema,
  }),
  dependsOn: ['admin.audit'],
  budget: { requests: 1, statements: 3, bytes: 80_000, p95Ms: 150 },
  summary: 'Audit › Access: bounded counts, one page of sign-in events by cursor and freshness',
})
export type AuditAccessQuery = ScreenQuery<typeof adminAuditAccessScreen>
export type AuditAccessData = ScreenData<typeof adminAuditAccessScreen>
